import React, { useEffect, useMemo, useRef, useState } from "react";
import { RouteSegmentPointsList } from "../../generated/gpb/api/route";
import { RouteSegmentIteration, useGetRouteMetadataQuery, useGetRouteSegmentPointsQuery } from "../../services/routing";
import { useParams } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useSelectedTime } from "../../features/surface/surfaceSlice";
import { GeoMarkers } from "./GeoMarkers";
import { GeoPoints } from "./GeoPoints";
import { RouteId } from "../../generated/gpb/api/id";
import { MapRef } from "react-map-gl";
import { generatePoints } from "./geoUtils";

type GeoRouteProps = {
    propRouteId: RouteId | undefined,
    routeNumber: number,
    mapRef: React.RefObject<MapRef>,
    isSelected: boolean,
};

/**
 * Component to a route with markers for the RV, weather delays, overnight stops, and the final destination
 *
 * @param {string | undefined} propRouteId - A route ID passed as a prop.
 */
const GeoRoute: React.FC<GeoRouteProps> = ( { propRouteId, routeNumber, mapRef, isSelected } ) =>
{
    const urlRouteId = useParams().routeId;
    const routeId = propRouteId !== undefined ? propRouteId.hexString : urlRouteId;

    const { data: routeMetadata } = useGetRouteMetadataQuery( routeId ?? skipToken );

    const [ routePoints, setRoutePoints ] = useState<RouteSegmentPointsList>( { segments: [] } );
    //using ref here because the accumulatedSegments are not directly used in rendering
    const accumulatedSegmentsRef = useRef<RouteSegmentPointsList>( { segments: [] } );

    const pointsFinished = routeMetadata !== undefined &&
        accumulatedSegmentsRef.current.segments.length >= routeMetadata.totalSegments;
    const {
        isFetching,
        data: routePointsData,
    } = useGetRouteSegmentPointsQuery(
        routeId !== undefined && !pointsFinished
            ? {
                id: routeId,
                index: accumulatedSegmentsRef.current.segments.length,
                count: RouteSegmentIteration,
            }
            : skipToken
    );

    useEffect( () =>
    {
        // Reset routePoints and pointsFinished when routeId changes
        setRoutePoints( { segments: [] } );
        accumulatedSegmentsRef.current = { segments: [] };
    }, [ routeId ] );

    useEffect( () =>
    {
        if ( routePointsData !== undefined && routeMetadata !== undefined && !isFetching && !pointsFinished )
        {
            accumulatedSegmentsRef.current.segments = [
                ...accumulatedSegmentsRef.current.segments,
                ...routePointsData.segments,
            ];

            const totalReceived = accumulatedSegmentsRef.current.segments.length;
            if ( totalReceived >= routeMetadata.totalSegments )
            {
                setRoutePoints( { segments: accumulatedSegmentsRef.current.segments } );
            }
        }
    }, [ routePointsData, routeMetadata, isFetching, pointsFinished ] );


    const points = useMemo( () => generatePoints( routePoints ), [ routePoints ] );

    const selectedTime = useSelectedTime();

    return (
        <React.Fragment>
            <GeoPoints
                mapRef = {mapRef}
                routePoints = {routePoints}
                routeNumber = {routeNumber}
                isSelected = {isSelected}
                routeMetadata = {routeMetadata}
            />
            {isSelected &&
            <GeoMarkers
                routePoints = {routePoints}
                points = {points}
                selectedTime = {selectedTime}
                routeMetadata = {routeMetadata}
            />}
        </React.Fragment>
    );
};

export default GeoRoute;
